import moment from 'moment'
import { useEffect, useRef, useState } from 'react'
import { api } from '../../core/api/ConnectionManager'
import { colors, palette } from 'theme'
import translate from 'core/translate'
import { RootStateOrAny, useSelector } from 'react-redux'
import helpers from 'core/helpers'
import { getURLSearchParamsByLocation } from 'features/routes/utils'

const NOTIFICATIONS_PAGE_SIZE = 30
const MAX_NOTIFICATIONS_IN_MEMORY = 1000

export interface INotificationFilterFields {
    status: string[]
    type: string[]
    category: string[]
    startDate?: string
    endDate?: string
}

const getNotificationFiltersFromLocation = (location: any): INotificationFilterFields => {
    const query = helpers.getQuery(getURLSearchParamsByLocation(location), 'notificationCenter')
    const splitValues = (value?: string) => (value ? value.split('//').filter(Boolean) : [])

    return {
        status: splitValues(query.status),
        type: splitValues(query.type),
        category: splitValues(query.category),
        startDate: query.startDate,
        endDate: query.endDate,
    }
}

const mergeNotifications = (current: INotification[], incoming: INotification[], prepend: boolean): INotification[] => {
    const seen = new Set<number>()
    const merged = prepend ? [...incoming, ...current] : [...current, ...incoming]

    return merged
        .filter((notification) => {
            if (seen.has(notification.id)) return false

            seen.add(notification.id)
            return true
        })
        .slice(0, MAX_NOTIFICATIONS_IN_MEMORY)
}

export interface INotification {
    category: string
    companyId: number
    context: { [index: string]: any }
    createdAt: string
    defaultMessage: null | { [index: string]: any }
    id: number
    localeMessage: null | { [index: string]: any }
    status: string
    type: string
    updatedAt: string
}
export interface IState {
    tabs: {
        name: string
        permission: string
    }[]
    selectedTab: string
    notifications: INotification[]
    isModalActive: boolean
    activeModalId: null | number
    notificationInModal: null | INotification
    startDate: string
    endDate: string
    apply: boolean
    token: string
    selectedNotifications: number[]
}
export const NotificationСenterHooks = (location: any) => {
    const user = useSelector((state: RootStateOrAny) => state.user)
    const [state, setState] = useState<IState>({
        tabs: [
            {
                name: 'notificationCenter',
                permission: 'notificationCenter',
            },
            {
                name: 'usersActionsHistory',
                permission: 'userHistory',
            },
            {
                name: 'notificationEmails',
                permission: 'emailHistory',
            },
        ],
        selectedTab: 'notificationCenter',
        notifications: [],
        isModalActive: false,
        activeModalId: null,
        notificationInModal: null,
        startDate: moment().format('YYYY-MM-DD'),
        endDate: moment().add(1, 'day').format('YYYY-MM-DD'),
        apply: false,
        token: '',
        selectedNotifications: [],
    })
    const [filterFields, setFilterFields] = useState<INotificationFilterFields>(() =>
        getNotificationFiltersFromLocation(location)
    )
    const isMountedRef = useRef(true)
    const isLoadingNotificationsRef = useRef(false)
    const notificationsRequestIdRef = useRef(0)
    const userPermissions = useSelector((state: RootStateOrAny) => state.user.data.permissions)

    useEffect(() => {
        return () => {
            isMountedRef.current = false
            notificationsRequestIdRef.current += 1
        }
    }, [])

    useEffect(() => {
        const nextFilters = getNotificationFiltersFromLocation(location)

        setFilterFields((currentFilters) =>
            JSON.stringify(currentFilters) === JSON.stringify(nextFilters) ? currentFilters : nextFilters
        )
    }, [location.search])

    let notificationsPermissions = userPermissions.find(
        (element: { [index: string]: any }) => element.name === 'notificationCenter'
    )
    let isDeletePermission = notificationsPermissions
        ? notificationsPermissions.actions.find((element: { [index: string]: any }) => element.name === 'delete')
        : null

    const getKeepDurationSettings = (field: string, defaultValue: string) => {
        if (user && user.company && user.company.notificationCenter[field]) {
            return user.company.notificationCenter[field]
        }

        return defaultValue
    }
    const getCardBg = (type: string) => {
        switch (type) {
            case 'information':
                return colors.greyLight
            case 'warning':
                return palette.brandBlue
            case 'error':
                return colors.notificationRed
            default:
                return colors.greyLight
        }
    }

    const isInformationType = (type: string) => {
        switch (type) {
            case 'information':
                return colors.blackLight
            default:
                return colors.white
        }
    }

    const getNotificationContentByCode = (
        notification: {
            id: number
            notificationCode: number
            context: { expiredDays: number; instance: any }
        },
        field: 'title' | 'name' | 'text'
    ) => {
        const { notificationCode } = notification
        let resultObject = {
            title: '',
            name: '',
            text: '',
        }
        switch (notificationCode) {
            case 1:
                const videoParamsName = Array.isArray(notification.context.instance)
                    ? '-'
                    : notification.context.instance.name
                resultObject = {
                    title: translate('videoParamsWarning'),
                    name: videoParamsName,
                    text: translate('videoParamsNotificationText'),
                }
                break
            case 2:
                resultObject = {
                    title: `${translate('licenseEndingParamsWarning')} ${
                        notification.context.expiredDays
                    } ${helpers.getLabelByNumber(notification.context.expiredDays, [
                        translate('dayN1'),
                        translate('dayN2'),
                        translate('dayN3'),
                    ])}`,
                    name: '-',
                    text: translate('licenseEndingParamsNotificationText'),
                }
                break
            case 3:
                resultObject = {
                    title: translate('licensePasswordSecurity'),
                    name: '-',
                    text: translate('licensePasswordSecurityText'),
                }
                break
            case 4:
                resultObject = {
                    title: translate('passwordUpdate'),
                    name: '-',
                    text: translate('passwordUpdate'),
                }
                break
            case 5:
                resultObject = {
                    title: translate('technicalSupportConnected'),
                    name: '-',
                    text: translate('technicalSupportConnected'),
                }
                break
            case 6:
                resultObject = {
                    title: translate('dsNoLicense'),
                    name: '-',
                    text: translate('dsNoLicense'),
                }
                break
            case 7:
                resultObject = {
                    title: translate('dsTimezoneUpdated'),
                    name: '-',
                    text: translate('dsTimezoneUpdated'),
                }
                break
            case 8:
                const displayName = Array.isArray(notification.context.instance)
                    ? notification.context.instance.reduce((prev, curr) => (prev += `${curr.name}, `), '')
                    : '-'
                resultObject = {
                    title: displayName,
                    name: displayName,
                    text: translate('dsOfflineNotificationWarning'),
                }
                break
            case 9:
                const broadcastName =
                    (notification.context && notification.context.instance && notification.context.instance.title) ||
                    '-'
                resultObject = {
                    title: broadcastName,
                    name: broadcastName,
                    text: translate('diffFpsInBroadcastOrAdvertisement'),
                }
                break
            default:
                break
        }

        return resultObject[field] || ''
    }

    const modelText = (notification: any, field: 'title' | 'name' | 'text') => {
        if (notification && notification.localeMessage && notification.localeMessage[field]) {
            return notification.localeMessage[field]
        } else if (notification && notification.defaultMessage && notification.defaultMessage[field]) {
            return notification.defaultMessage[field]
        } else if (notification && notification.context && notification.notificationCode) {
            return getNotificationContentByCode(notification, field)
        } else if (
            notification &&
            notification.context &&
            notification.context.instance &&
            notification.context.instance[field]
        ) {
            return notification.context.instance[field]
        } else {
            return '-'
        }
    }

    const getCardIcon = (category: string, type: string) => {
        if (type === 'error') {
            return 'notificationsError'
        }
        switch (category) {
            case 'license':
                return 'licenses'
            case 'digitalSignage':
                return 'display'
            case 'engineeringWorks':
                return 'notificationsBuild'
            case 'broadcast':
                return 'camera'
            case 'content':
                return 'files'
            default:
                return 'notificationsWarning'
        }
    }
    const getFilterParam = (filters: INotificationFilterFields) => {
        const appliedFilters: any[] = []

        ;(['status', 'type', 'category'] as const).forEach((name) => {
            if (!filters[name].length) return

            appliedFilters.push({
                type: 'select',
                name,
                options: filters[name].map((value) => ({ value, selected: true })),
            })
        })

        if (filters.startDate || filters.endDate) {
            appliedFilters.push({
                type: 'range',
                name: 'createdAt',
                minValue: moment(filters.startDate || state.startDate).format('YYYY-MM-DD'),
                maxValue: moment(filters.endDate || state.endDate).format('YYYY-MM-DD'),
            })
        }

        return appliedFilters.length ? JSON.stringify(appliedFilters) : ''
    }
    const getNotifications = (isLazyLoad: boolean, hasLimit: boolean = true) => {
        if (isLazyLoad && (isLoadingNotificationsRef.current || !state.token)) return

        const requestId = notificationsRequestIdRef.current + 1
        const requestedLimit = hasLimit
            ? isLazyLoad
                ? Math.min(state.notifications.length + NOTIFICATIONS_PAGE_SIZE, MAX_NOTIFICATIONS_IN_MEMORY)
                : NOTIFICATIONS_PAGE_SIZE
            : undefined
        notificationsRequestIdRef.current = requestId
        isLoadingNotificationsRef.current = true

        let appliedFilters: string = getFilterParam(filterFields)
        let payload: {
            sort: string[]
            limit?: number
        } = {
            sort: ['createdAt', 'DESC'],
        }

        if (requestedLimit) {
            payload.limit = requestedLimit
        }

        api.send(
            'getNotifications',
            appliedFilters === ''
                ? {
                      ...payload,
                  }
                : {
                      ...payload,
                      appliedFilters,
                  }
        )
            .then((res: any) => {
                if (!isMountedRef.current || requestId !== notificationsRequestIdRef.current || !res?.data) return

                setState((prevState) => {
                    const notifications = isLazyLoad
                        ? mergeNotifications(prevState.notifications, res.data, false)
                        : mergeNotifications([], res.data, false)
                    const didAppend = notifications.length > prevState.notifications.length
                    const reachedLimit = notifications.length >= MAX_NOTIFICATIONS_IN_MEMORY

                    return {
                        ...prevState,
                        notifications,
                        token:
                            isLazyLoad &&
                            (!didAppend ||
                                reachedLimit ||
                                (requestedLimit !== undefined && res.data.length < requestedLimit))
                                ? ''
                                : res.nextPageToken,
                    }
                })
            })
            .then(
                () => {
                    if (requestId === notificationsRequestIdRef.current) {
                        isLoadingNotificationsRef.current = false
                    }
                },
                () => {
                    if (requestId === notificationsRequestIdRef.current) {
                        isLoadingNotificationsRef.current = false
                    }
                }
            )
    }

    const deleteNotification = (payload: { notificationId: number | number[] }) =>
        api.send('deleteNotifications', payload)

    useEffect(() => {
        const listenersId: string[] = []

        api.addObserver(
            'notificationsDeleted',
            (res: any) => {
                const deletedIds = Array.isArray(res.notificationId) ? res.notificationId : [res.notificationId]

                setState((prevState) => ({
                    ...prevState,
                    notifications: prevState.notifications.filter(
                        (notification) => !deletedIds.includes(notification.id)
                    ),
                    selectedNotifications: prevState.selectedNotifications.filter((id) => !deletedIds.includes(id)),
                }))
            },
            listenersId
        )

        api.addObserver(
            'notificationsCreated',
            (res: any) => {
                setState((prevState) => ({
                    ...prevState,
                    notifications: mergeNotifications(prevState.notifications, [res], true),
                }))
            },
            listenersId
        )

        api.addObserver(
            'notificationsUpdated',
            (res: any) => {
                if (typeof res.id !== 'number') return

                setState((prevState) => ({
                    ...prevState,
                    notifications: prevState.notifications.map((notification) =>
                        notification.id === res.id ? res : notification
                    ),
                    notificationInModal:
                        prevState.notificationInModal?.id === res.id ? res : prevState.notificationInModal,
                }))
            },
            listenersId
        )

        return () => {
            listenersId.forEach((id) => api.removeObserver(id))
        }
    }, [])

    useEffect(() => {
        getNotifications(false)
    }, [filterFields])

    const readNotifications = (payload: { notificationId: number | number[] }) => {
        api.send('readNotifications', payload).then(() => {
            if (!isMountedRef.current) return

            const readIds = Array.isArray(payload.notificationId) ? payload.notificationId : [payload.notificationId]
            setState((prevState) => ({
                ...prevState,
                notifications: prevState.notifications.map((notification) =>
                    readIds.includes(notification.id) ? { ...notification, status: 'read' } : notification
                ),
            }))
        })
    }

    useEffect(() => {
        if (!state.activeModalId) return

        const notification = state.notifications.find((item) => item.id === state.activeModalId)
        if (!notification) return

        if (state.notificationInModal !== notification) {
            setState((prevState) => ({ ...prevState, notificationInModal: notification }))
        }

        if (notification.status === 'unread') {
            readNotifications({ notificationId: notification.id })
        }
    }, [state.activeModalId, state.notificationInModal, state.notifications])
    const onSelect = (id: number, isChecked: boolean) => {
        setState((prevState) => ({
            ...prevState,
            selectedNotifications: isChecked
                ? Array.from(new Set([...prevState.selectedNotifications, id]))
                : prevState.selectedNotifications.filter((selectedId) => selectedId !== id),
        }))
    }

    const onSelectAll = (isChecked: boolean) => {
        setState((prevState) => ({
            ...prevState,
            selectedNotifications: isChecked ? prevState.notifications.map((item) => item.id) : [],
        }))
    }

    const deleteSelectedNotifications = () => {
        deleteNotification({ notificationId: state.selectedNotifications })
    }

    const readSelected = () => {
        readNotifications({ notificationId: state.selectedNotifications })
    }

    const getAllNotifications = () => {
        getNotifications(false, false)
    }

    return {
        getCardBg,
        isInformationType,
        getCardIcon,
        getNotifications,
        deleteNotification,
        state,
        setState,
        filterFields,
        setFilterFields,
        modelText,
        isDeletePermission,
        onSelect,
        onSelectAll,
        deleteSelectedNotifications,
        readSelected,
        getAllNotifications,
        getKeepDurationSettings,
    }
}
